import { fetchApi } from "~vue/api";
import type {
    DevelopmentGoal,
    MarkDevelopmentGoalAchievedRequest,
    StopPursuingDevelopmentGoalRequest,
    UpdateDevelopmentGoalRequest,
} from "~vue/api/generated/development-journeys";
import type { DevelopmentGoalUpdateProposal } from "~vue/types/chat";
import { formatDate } from "~vue/utils/formatDate";
import type { ComposerTranslation } from "vue-i18n";

type GoalUpdateRequest = { path: string; method: "POST" | "PATCH"; body: MarkDevelopmentGoalAchievedRequest | StopPursuingDevelopmentGoalRequest | UpdateDevelopmentGoalRequest };

const goalUpdateRequest = ({ goal_id, change, target_date }: DevelopmentGoalUpdateProposal): GoalUpdateRequest => {
    const goalPath = `/v1/development-journeys/goals/${goal_id}`;
    switch (change) {
        case "achieved":
            return { path: `${goalPath}/mark-achieved`, method: "POST", body: {} };
        case "no_longer_pursuing":
            return { path: `${goalPath}/stop-pursuing`, method: "POST", body: {} };
        case "target_date":
            return { path: goalPath, method: "PATCH", body: { target_date } };
    }
};

const isSavedGoalUpdate = (proposal: DevelopmentGoalUpdateProposal, goal: Pick<DevelopmentGoal, "status" | "target_date">) =>
    proposal.change === "target_date" ? goal.target_date === proposal.target_date : goal.status === proposal.change;

/**
 * Saves a confirmed change through the existing goal endpoints. Status changes leave a closed goal
 * as it is, so the returned goal is checked before the change is reported as saved.
 */
export async function saveDevelopmentGoalUpdate(proposal: DevelopmentGoalUpdateProposal): Promise<DevelopmentGoal | null> {
    const { path, method, body } = goalUpdateRequest(proposal);
    try {
        const response = await fetchApi(path, { method, body: JSON.stringify(body) });
        if (!response.ok) {
            return null;
        }
        const goal = (await response.json()) as DevelopmentGoal;
        return isSavedGoalUpdate(proposal, goal) ? goal : null;
    } catch {
        return null;
    }
}

/** The question the card asks before a change is confirmed, as Markdown. */
export function developmentGoalUpdateQuestion(t: ComposerTranslation, proposal: DevelopmentGoalUpdateProposal): string {
    if (proposal.change === "target_date") {
        return t("developmentJourneys.goalProgress.goalUpdate.question.targetDate", { date: formatDate(proposal.target_date ?? "", "date-long") });
    }
    return proposal.change === "achieved"
        ? t("developmentJourneys.goalProgress.goalUpdate.question.achieved")
        : t("developmentJourneys.goalProgress.goalUpdate.question.noLongerPursuing");
}
